import {
  CreditCardIcon,
  SparklesIcon,
  ClockIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { useClerkCustomer } from '../hooks/useClerkOps';
import { formatMoney } from '../services/clerkOpsService';

function fmtRelative(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days < 30 ? `${days}d ago` : d.toLocaleDateString();
}

const STATUS_TONE: Record<string, string> = {
  active: 'text-success',
  past_due: 'text-warning',
  canceled: 'text-danger',
  paid: 'text-success',
  overdue: 'text-danger',
};

function Row({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-ink-3">{label}</span>
      <span className={tone ?? 'text-ink'}>{value}</span>
    </div>
  );
}

/**
 * Clerk business context for one customer, shown in the conversation panel in
 * place of e-commerce data. Renders subscription + activity for service-business
 * stores (gated upstream by useClerkOpsEnabled in CustomerPanel).
 */
export default function ClerkCustomerInfo({ customerId }: { customerId: string | null }) {
  const { data, isLoading } = useClerkCustomer(customerId);

  if (isLoading || !data) return null;

  if (!data.linked) {
    return (
      <div>
        <h4 className="mb-3 font-display text-sm font-semibold text-ink">Subscription</h4>
        <div className="rounded-card bg-surface-2 p-3 text-sm text-ink-3">
          Not linked to a Clerk client yet.
          {data.reason === 'no_match' && ' No client matches this contact’s phone or email.'}
        </div>
      </div>
    );
  }

  const c = data.client;
  const b = data.billing;
  const fp = c?.featureProgress;

  return (
    <div className="space-y-6">
      {/* Subscription */}
      <div>
        <h4 className="mb-3 flex items-center gap-1.5 font-display text-sm font-semibold text-ink">
          <CreditCardIcon className="h-4 w-4 text-ink-3" /> Subscription
        </h4>
        <div className="space-y-2 rounded-card bg-surface-2 p-3">
          <Row label="Plan" value={b?.plan ?? '—'} />
          <Row
            label="Status"
            value={b?.subscriptionStatus ?? c?.status ?? '—'}
            tone={STATUS_TONE[(b?.subscriptionStatus ?? c?.status ?? '').toLowerCase()]}
          />
          <Row label="Balance" value={formatMoney(b?.balance ?? null)} />
          {b?.nextInvoiceAt && <Row label="Next invoice" value={new Date(b.nextInvoiceAt).toLocaleDateString()} />}
        </div>
      </div>

      {/* Activity */}
      <div>
        <h4 className="mb-3 flex items-center gap-1.5 font-display text-sm font-semibold text-ink">
          <SparklesIcon className="h-4 w-4 text-ink-3" /> Activity
        </h4>
        <div className="space-y-2 rounded-card bg-surface-2 p-3">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5 text-ink-3">
              <ClockIcon className="h-4 w-4" /> Last active
            </span>
            <span className="text-ink">{fmtRelative(c?.lastActiveAt ?? null)}</span>
          </div>
          <Row label="Latest action" value={c?.lastAction?.label ?? '—'} />
          {fp && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-ink-3">Features used</span>
              <div className="flex items-center gap-2">
                <div className="h-2 w-16 overflow-hidden rounded-full bg-surface-2">
                  <div
                    className="h-full bg-brand"
                    style={{ width: `${fp.total > 0 ? Math.round((fp.used / fp.total) * 100) : 0}%` }}
                  />
                </div>
                <span className="text-xs text-ink-3">
                  {fp.used}/{fp.total}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent invoices */}
      {b && b.invoices.length > 0 && (
        <div>
          <h4 className="mb-3 flex items-center gap-1.5 font-display text-sm font-semibold text-ink">
            <DocumentTextIcon className="h-4 w-4 text-ink-3" /> Recent Invoices
          </h4>
          <div className="space-y-1.5">
            {b.invoices.slice(0, 5).map((inv) => (
              <div key={inv.invoiceId} className="flex items-center justify-between text-sm">
                <span className="text-ink-3">{inv.issuedAt ? new Date(inv.issuedAt).toLocaleDateString() : inv.invoiceId}</span>
                <span className="text-ink">{formatMoney(inv.amount)}</span>
                <span className={STATUS_TONE[(inv.status ?? '').toLowerCase()] ?? 'text-ink-3'}>
                  {inv.status ?? '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
